import type { CopySet } from './types';
import type { CopyEditBaseline, CopyEditFields } from './copyEditConfirmation';

export const DRAFT_COPY_GROUP = 'Drafts (not yet approved by Kyu)';
export const DRAFT_COPY_BADGE = 'Draft · One-off';

export function copyMessageLabel(copy: Pick<CopySet, 'name' | 'review_state'>): string {
  return `${copy.name}${copy.review_state === 'draft' ? ' (Draft)' : ''}`;
}

export function ordinaryCopyChoices(copies: CopySet[]): CopySet[] {
  return copies.filter((copy) => copy.archived_at == null && copy.review_state !== 'draft');
}

export function copyPickerGroups(copies: CopySet[], allowDraft: boolean) {
  return [
    {
      label: DRAFT_COPY_GROUP,
      options: copies
        .filter((copy) => copy.review_state === 'draft')
        .map((copy) => ({
          value: copy.id,
          label: copyMessageLabel(copy),
          disabled: !allowDraft || copy.archived_at != null,
        })),
    },
  ];
}

export function copyEditFields(copy: CopySet): CopyEditFields {
  const fields: Record<string, string | null> = {
    name: copy.name.trim(),
    description: copy.description,
    type: copy.type,
    channel: copy.channel,
  };
  for (let slot = 1; slot <= 8; slot += 1) {
    const keys = [`e${slot}_subject`, `e${slot}_body`] as const;
    for (const key of keys) fields[key] = (copy[key as keyof CopySet] as string | null) ?? '';
  }
  return fields;
}

export function copyEditBaseline(copy: CopySet): CopyEditBaseline {
  if (!copy.message_revision || typeof copy.message_used !== 'boolean') {
    throw new Error('copy_catalog_revision_missing');
  }
  return {
    id: copy.id,
    revision: copy.message_revision,
    used: copy.message_used,
    fields: copyEditFields(copy),
  };
}

/** Views have conservative nullable generated types. Validate, do not silently
 * cast an incomplete catalog into a usable editor or guess its concurrency ID. */
export function decodeCopySet(value: unknown, catalog = false): CopySet {
  if (value === null || typeof value !== 'object' || Array.isArray(value))
    throw new Error('copy_catalog_incomplete');
  const row = value as Record<string, unknown>;
  for (const key of [
    'id',
    'name',
    'description',
    'type',
    'channel',
    'created_at',
    'updated_at',
    'message_revision',
    'e1_subject',
    'e1_body',
    'e2_subject',
    'e2_body',
    'e3_subject',
    'e3_body',
    'e4_subject',
    'e4_body',
  ]) {
    if (typeof row[key] !== 'string') throw new Error('copy_catalog_incomplete');
  }
  for (const key of [
    'sheet_id',
    'created_by',
    'updated_by',
    'archived_at',
    'reviewed_by',
    'reviewed_at',
    'e5_subject',
    'e5_body',
    'e6_subject',
    'e6_body',
    'e7_subject',
    'e7_body',
    'e8_subject',
    'e8_body',
  ]) {
    if (row[key] !== null && typeof row[key] !== 'string')
      throw new Error('copy_catalog_incomplete');
  }
  if (
    typeof row.is_one_off !== 'boolean' ||
    !['draft', 'library'].includes(String(row.review_state)) ||
    (catalog && typeof row.message_used !== 'boolean')
  )
    throw new Error('copy_catalog_incomplete');
  return row as CopySet;
}
